import { I18nUtil } from "@utils/i18n";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "next/navigation";
import { useSnapshot } from "valtio";
import { usePushRetainModal } from "./usePushRetainModal";
import { useMemberRetentionStats } from "./useMemberRetentionStats";
import { useMemberUser } from "./useMemberUser";
import Translations from "@translate/hooks/useChatRetainModal";
import { PricingRetainModalCallable } from "@/src/components/AlphaPick/AlphaPickPricing";
import type { MemberRetentionStats } from "@/src/features/api/memberApi";
import { isValidLocale, type Locale } from "@/src/utils/i18n.locale";
import { rootStore } from "@/src/store";
import { useIsPc } from "./useIsPc";

const I18n = I18nUtil.createScoped(Translations);

const AUTO_OPEN_RENEW_MODAL_STORAGE_KEY = "auto_open_renew_modal_cycle";
const AUTO_OPEN_RENEW_MODAL_DELAY_MS = 2500;

/**
 * auto / push 都属于“被动展示”的 retain modal。
 * 这类展示在同一个 Web 会话里只允许成功展示一次，避免：
 * 1. App 首次进入后的 auto 弹窗刚弹完
 * 2. 紧接着用户点击 retention push 又再弹一次
 *
 * 注意：
 * - 手动点击 Renew 不受这层锁限制
 * - 这里使用模块级变量而不是 hook state，确保多个 hook 实例共享同一把锁
 */
let hasShownPassiveRetainModalInSession = false;
let isPassiveRetainModalOpening = false;

const toCount = (value?: number | null) => Number(value ?? 0);

const sumCountFieldsByPrefix = (
  retentionStats: MemberRetentionStats | undefined,
  prefix: string,
) => {
  if (!retentionStats) {
    return 0;
  }
  return Object.entries(retentionStats).reduce((sum, [key, value]) => {
    if (!key.startsWith(prefix) || !key.endsWith("_count")) {
      return sum;
    }
    return sum + toCount(value as number | null | undefined);
  }, 0);
};

const getFallbackFeatures = (locale?: Locale) => {
  return [
    I18n.t("Monitors your portfolio while you're away", { locale }),
    I18n.t("Flags risks before they land", { locale }),
    I18n.t("Answers built on your unique history.", { locale }),
  ];
};

const getRemainDays = (remainMinutes?: number | null) => {
  const safeMinutes = Number(remainMinutes ?? 0);
  if (safeMinutes <= 0) {
    return 0;
  }
  return Math.max(1, Math.floor(safeMinutes / (24 * 60)));
};

const getRetainSubtitle = ({
  isTrial,
  isExpert,
  locale,
  remainMinutes,
}: {
  isTrial?: boolean;
  isExpert?: boolean;
  locale?: Locale;
  remainMinutes?: number | null;
}) => {
  if (isTrial) {
    return I18n.t(
      "Your trial ends tomorrow. Here’s a look at what we accomplished together.",
      {
        locale,
      },
    );
  }

  const remainDays = getRemainDays(remainMinutes);
  const planName = isExpert ? "Expert" : "Pro";
  if (remainDays === 1) {
    return I18n.t("Your %{var0} expires in 1 day.", {
      locale,
      var0: planName,
    });
  }
  return I18n.t("Your %{var0} expires in %{var1} days.", {
    locale,
    var0: planName,
    var1: remainDays,
  });
};

const getRetainTitle = (memberDays?: number | null, locale?: Locale) => {
  const safeDays = Math.max(0, Number(memberDays ?? 0));
  return I18n.t("We've known each other for %{var0} days!", {
    locale,
    var0: safeDays,
  });
};

/**
 * 用“用户 + 当前会员等级 + 本次到期时间”组成一个稳定 key，
 * 用来标识“这个到期周期”是否已经自动弹过续费挽留弹窗。
 */
const getAutoOpenRenewCycleKey = ({
  ownerId,
  expireTime,
  level,
}: {
  ownerId?: string;
  expireTime?: number | null;
  level?: string;
}) => {
  return [ownerId ?? "", level ?? "", expireTime ?? ""].join(":");
};

const buildRetainModalCopyOverrides = (
  retentionStats?: MemberRetentionStats,
  memberInfo?: {
    isTrial?: boolean;
    isExpert?: boolean;
    remain_minutes?: number | null;
  },
  locale?: Locale,
) => {
  const memberDays = Number(retentionStats?.member_days ?? 0);
  const chatsAndAlerts =
    toCount(retentionStats?.chat_count) + toCount(retentionStats?.signal_count);
  const alpha = sumCountFieldsByPrefix(retentionStats, "alpha_");
  const deepReports = sumCountFieldsByPrefix(retentionStats, "report_");
  const summaryFeatures = (retentionStats?.summary ?? [])
    .map((item) => item?.trim())
    .filter(Boolean) as string[];

  return {
    title: getRetainTitle(memberDays, locale),
    subtitle: getRetainSubtitle({
      isTrial: memberInfo?.isTrial,
      isExpert: memberInfo?.isExpert,
      locale,
      remainMinutes: memberInfo?.remain_minutes,
    }),
    userData: [
      { value: chatsAndAlerts, label: I18n.t("Chats & Alerts") },
      { value: alpha, label: I18n.t("Ed's Picks") },
      { value: deepReports, label: I18n.t("Deep Reports") },
    ],
    features:
      summaryFeatures.length > 0
        ? summaryFeatures
        : getFallbackFeatures(locale),
  };
};

export const useChatRetainModal = ({
  isClientReady = true,
}: {
  isClientReady?: boolean;
} = {}) => {
  const { ownerInfo } = useSnapshot(rootStore.user);
  const { data: memberInfo } = useMemberUser();
  const { data: retentionStats, refetch: refetchRetentionStats } =
    useMemberRetentionStats({ enabled: false });
  const { locale: localeParam } = useParams<{ locale: string }>();
  const locale = isValidLocale(localeParam) ? localeParam : undefined;
  const autoOpenTimerRef = useRef<number | null>(null);
  const pendingAutoCycleKeyRef = useRef<string | null>(null);
  const [isPC] = useIsPc();
  const shouldShowRenew = useMemo(() => {
    const remainMinutes = Number(memberInfo?.remain_minutes ?? 0);
    return (
      !memberInfo?.auto_renewal && remainMinutes > 0 && remainMinutes <= 48 * 60
    );
  }, [memberInfo?.auto_renewal, memberInfo?.remain_minutes]);
  const isRetainContextReady =
    Boolean(ownerInfo?.id) && memberInfo !== undefined;

  const retainModalCopyOverrides = useMemo(
    () =>
      buildRetainModalCopyOverrides(
        retentionStats,
        memberInfo,
        locale as Locale,
      ),
    [locale, memberInfo, retentionStats],
  );

  /**
   * 真正执行 retain modal 打开的底层方法。
   *
   * 设计上分两层：
   * 1. `openRetainModal`：给手动 Renew 使用，不受 session 锁限制
   * 2. `tryAutoOpenRetainModal` / push pending effect：给被动展示使用
   *
   * 两者最终都复用这里，保证拉 retention stats 与弹窗入参始终一致。
   */
  const openRetainModalInternal = useCallback(
    async (debug?: boolean) => {
      if (!shouldShowRenew && !debug) {
        return false;
      }

      const refetchResult = await refetchRetentionStats();
      const latestRetentionStats = refetchResult.data ?? retentionStats;
      const latestRetainModalCopyOverrides = buildRetainModalCopyOverrides(
        latestRetentionStats,
        memberInfo,
        locale as Locale,
      );

      await PricingRetainModalCallable({
        needCreditAmount: 1,
        isPC,
        trackingModuleName: "edgen_chat_memu_renew",
        retainModalCopyOverrides: latestRetainModalCopyOverrides,
      });
      return true;
    },
    [
      isPC,
      locale,
      memberInfo,
      refetchRetentionStats,
      retentionStats,
      shouldShowRenew,
    ],
  );

  /**
   * 尝试抢占“被动展示”的会话级锁。
   *
   * 为什么需要这把锁：
   * - auto 与 push 可能几乎同时到达
   * - 两者展示的是同一个 retain modal
   * - 如果不先抢锁，可能在一次会话内连续弹两次，体验会很差
   */
  const acquirePassiveRetainModalLock = useCallback(() => {
    if (hasShownPassiveRetainModalInSession || isPassiveRetainModalOpening) {
      return false;
    }
    isPassiveRetainModalOpening = true;
    return true;
  }, []);

  const releasePassiveRetainModalLock = useCallback((shown: boolean) => {
    isPassiveRetainModalOpening = false;
    if (shown) {
      hasShownPassiveRetainModalInSession = true;
    }
  }, []);

  /**
   * 供 push 专用 hook 查询当前“被动展示”是否可用。
   *
   * 单独抽成 callback 的目的是把 push 相关逻辑移到独立文件后，
   * 仍然能复用同一把 session 级展示锁，而不用把锁逻辑也复制一份。
   */
  const isPassiveRetainModalBlocked = useCallback(() => {
    return hasShownPassiveRetainModalInSession || isPassiveRetainModalOpening;
  }, []);

  /**
   * auto / push 共用的“被动展示”打开逻辑。
   * 它与手动打开的唯一区别是：需要服从会话级展示锁。
   */
  const openPassiveRetainModal = useCallback(
    async ({
      onLockAcquired,
    }: {
      onLockAcquired?: () => void;
    } = {}) => {
      if (!acquirePassiveRetainModalLock()) {
        return false;
      }

      onLockAcquired?.();

      try {
        const shown = await openRetainModalInternal();
        releasePassiveRetainModalLock(shown);
        return shown;
      } catch (error) {
        releasePassiveRetainModalLock(false);
        throw error;
      }
    },
    [
      acquirePassiveRetainModalLock,
      openRetainModalInternal,
      releasePassiveRetainModalLock,
    ],
  );

  useEffect(() => {
    return () => {
      if (autoOpenTimerRef.current !== null) {
        window.clearTimeout(autoOpenTimerRef.current);
      }
    };
  }, []);

  const { hasPendingPushRetainRequest, requestOpenRetainModalFromPush } =
    usePushRetainModal({
      isRetainContextReady,
      shouldShowRenew,
      isPassiveRetainModalBlocked,
      openPassiveRetainModal,
    });

  /**
   * 当前到期周期内只允许自动弹一次。
   * 返回 true 表示这次确实触发了自动弹窗，false 表示已经弹过或缺少必要上下文。
   */
  const tryAutoOpenRetainModal = useCallback(() => {
    /**
     * 如果当前有 retention push 在等待数据 ready，则 auto 先让路。
     * 这样可以满足“push 优先于 auto”，同时仍然保证最终只展示一次。
     */
    if (hasPendingPushRetainRequest) {
      return false;
    }

    /**
     * 只对 auto retain 生效的“上游 ready”保护。
     *
     * 这里使用 RootLayoutClient 传入的 isClientReady，而不是 hook 内部自己 mount 一次就放行：
     * - RootLayoutClient 才真正控制着 EdgenModal / Drawer 等全局宿主的 hydrated 时机
     * - 这样不会影响手动 openRetainModal，只是让 auto retain 在全局 modal 宿主 ready 后再尝试
     */
    if (
      typeof window === "undefined" ||
      !isClientReady ||
      !ownerInfo?.id ||
      !shouldShowRenew
    ) {
      return false;
    }

    const cycleKey = getAutoOpenRenewCycleKey({
      ownerId: ownerInfo.id,
      expireTime: memberInfo?.expire_time,
      level: memberInfo?.level,
    });

    if (!cycleKey) {
      return false;
    }

    const storageKey = `${AUTO_OPEN_RENEW_MODAL_STORAGE_KEY}:${cycleKey}`;
    if (window.localStorage.getItem(storageKey)) {
      return false;
    }

    /**
     * 参考低信用弹窗的处理方式：auto retain 不在满足条件的当下立刻弹，
     * 而是延迟一段时间，等待 `EdgenModalCreateAble.Root` 经由
     * lazyHydrate + requestIdleCallback 完成挂载。
     *
     * 这里额外做两层保护：
     * 1. 同一个周期如果已经排队过一次 auto retain，就不重复排队
     * 2. 如果周期 key 变化，先清掉旧计时器，再以新周期重新排队
     */
    if (pendingAutoCycleKeyRef.current === cycleKey) {
      return false;
    }

    if (autoOpenTimerRef.current !== null) {
      window.clearTimeout(autoOpenTimerRef.current);
      autoOpenTimerRef.current = null;
      pendingAutoCycleKeyRef.current = null;
    }

    pendingAutoCycleKeyRef.current = cycleKey;

    autoOpenTimerRef.current = window.setTimeout(() => {
      autoOpenTimerRef.current = null;
      pendingAutoCycleKeyRef.current = null;

      /**
       * 周期去重不要早于“拿到被动展示锁”：
       * - 先拿锁，说明这次 auto retain 确实进入了被动展示流程
       * - 再写 localStorage，避免“其实没进展示流程，但周期已被记掉”
       */
      void openPassiveRetainModal({
        onLockAcquired: () => {
          window.localStorage.setItem(storageKey, "1");
        },
      });
    }, AUTO_OPEN_RENEW_MODAL_DELAY_MS);

    return true;
  }, [
    isClientReady,
    memberInfo?.expire_time,
    memberInfo?.level,
    openPassiveRetainModal,
    ownerInfo?.id,
    hasPendingPushRetainRequest,
    shouldShowRenew,
  ]);

  return {
    openRetainModal: openRetainModalInternal,
    requestOpenRetainModalFromPush,
    tryAutoOpenRetainModal,
    shouldShowRenew,
    retainModalCopyOverrides,
  };
};
